import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { StatusChip } from "@/components/StatusChip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X } from "lucide-react";

export default function AccessRequests() {
  const [reqs, setReqs] = useState(null);
  const [modules, setModules] = useState([]);
  const [active, setActive] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = async () => {
    try {
      const [r, m] = await Promise.all([
        api.get("/admin/access-requests", { params: { status: "pending" } }),
        api.get("/admin/modules"),
      ]);
      setReqs(r.data.requests);
      setModules(m.data.modules);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openApprove = (req) => { setActive(req); setSelected([]); };
  const approve = async () => {
    if (selected.length === 0) return toast.error("Select at least one module");
    try {
      await api.post(`/admin/access-requests/${active.id}/approve`, { module_ids: selected });
      toast.success("Access approved & modules assigned");
      setActive(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (req) => {
    try { await api.post(`/admin/access-requests/${req.id}/reject`); toast.success("Request rejected"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  if (!reqs) return <Loader />;

  return (
    <div className="space-y-6" data-testid="admin-access-requests">
      <h1 className="text-[32px] font-bold text-[#1c1b1b]">Access Requests</h1>
      {reqs.length === 0 ? (
        <p className="text-sm text-[#434654] bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="access-empty">No pending requests.</p>
      ) : (
        <div className="space-y-3">
          {reqs.map((r) => (
            <div key={r.id} data-testid={`access-request-${r.id}`} className="bg-white border border-[#E5E7EB] rounded-lg p-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-semibold text-[#1c1b1b]">{r.nurse_name}</p>
                <p className="text-sm text-[#434654]">{r.nurse_email}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusChip status="pending" />
                <button data-testid={`approve-btn-${r.id}`} onClick={() => openApprove(r)}
                  className="h-10 px-4 rounded-lg bg-[#003d9b] text-white font-semibold text-sm flex items-center gap-1 hover:bg-[#0052cc] transition-colors">
                  <Check className="w-4 h-4" /> Approve & Assign
                </button>
                <button data-testid={`reject-btn-${r.id}`} onClick={() => reject(r)}
                  className="h-10 px-4 rounded-lg border border-[#ba1a1a]/30 text-[#ba1a1a] font-semibold text-sm flex items-center gap-1 hover:bg-red-50 transition-colors">
                  <X className="w-4 h-4" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="assign-modules-dialog">
          <DialogHeader><DialogTitle>Assign Modules to {active?.nurse_name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {modules.length === 0 && <p className="text-sm text-[#434654]">No modules created yet. Create modules first.</p>}
            {modules.map((m) => (
              <label key={m.id} data-testid={`assign-module-${m.id}`} className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E7EB] cursor-pointer hover:bg-[#fcf9f8]">
                <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} className="w-4 h-4 accent-[#003d9b]" />
                <div className="flex-1">
                  <p className="font-medium text-[#1c1b1b]">{m.title}</p>
                  <p className="text-xs text-[#434654]">{m.slide_count} slides · {m.assessment_sets?.filter((s) => s.published).length || 0} published assessment(s)</p>
                </div>
              </label>
            ))}
          </div>
          <button data-testid="confirm-assign-btn" onClick={approve}
            className="w-full h-12 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors">
            Approve & Unlock
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
